'use client';

import { ReactNode } from 'react';
import styled from '@emotion/styled';
import {
	ArrowIconLight,
	Css,
	IResponsiveImageSource,
	IStyledCardProps,
	Responsive,
	StyledCardContent,
	StyledCardPicture,
} from '@/ui';

import BaseCard from '../BaseCard';
import { theme } from '../../../../styles/theme';
import { Color } from '../../../../styles/Color';
import {
	CardBody,
	CardLinkText,
	CardTitle,
	PublicationDate,
} from '../Card/Card';
import { renderMarkdownAsPlainText } from '../../../../utils/renderMarkdownAsPlainText';
import { formatFullDate } from '../../../../i18n/formatFullDate';

interface IBlogCardProps extends IStyledCardProps {
	title: string | ReactNode;
	body?: string | ReactNode;
	imageSource?: string | IResponsiveImageSource;
	linkHref: string;
	imageAlt?: string;
	publicationDate?: string;
}

export default function BlogCard({
	title,
	body,
	linkHref,
	imageSource,
	imageAlt,
	publicationDate,
	...props
}: IBlogCardProps) {
	const filteredBody = renderMarkdownAsPlainText(body);

	return (
		<BaseCard fullyClickable linkHref={linkHref} type="blog" {...props}>
			{imageSource && <CardPictureStyled source={imageSource} alt={imageAlt} />}
			<CardContentStyled>
				<div>
					<PublicationDate>
						{publicationDate && formatFullDate(publicationDate, 'MMM d, yyyy')}
					</PublicationDate>
					{typeof title === 'string' ? <CardTitle>{title}</CardTitle> : title}
					{filteredBody && <CardBody>{filteredBody}</CardBody>}
				</div>
				<CardLinkText>
					<span>Learn more</span> <ArrowIconLight />
				</CardLinkText>
			</CardContentStyled>
		</BaseCard>
	);
}

const CONTENT_CARD_WIDTH_DESKTOP = '80%';
const CONTENT_CARD_WIDTH_MOBILE = '90%';

const CardPictureStyled = styled(StyledCardPicture)`
	box-shadow: ${theme.card?.boxShadow};
	border-radius: ${theme.card?.borderRadius};

	img {
		aspect-ratio: 2/1;
		width: 100%;
		object-fit: cover;
		border-radius: 16px;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: ${Css.pxToRem(200)};
		}
	}
`;

const CardContentStyled = styled(StyledCardContent)`
	position: relative;
	max-width: ${CONTENT_CARD_WIDTH_DESKTOP};
	padding: ${Css.pxToRem(24)} ${Css.pxToRem(16)} ${Css.pxToRem(16)};
	border-radius: ${theme.card?.borderRadius};
	margin-top: -60px;
	background-color: ${Color.OnPrimarySurface};
	box-shadow: ${theme.card?.fullyClickable?.boxShadow};
	flex: 1;

	h3 {
		margin-bottom: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		padding: ${Css.pxToRem(16)};
		max-width: ${CONTENT_CARD_WIDTH_MOBILE};
	}
`;
